import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { provideTranslocoScope } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { Credentials, EmailAuthenticationService, EmailAuthenticationState, LinkAccountState } from '@platform-frontend/account';
import { ActionFilter, CustomValidators, ExclusiveActionFilter, FormWithEmail, LoadingState } from '@platform-frontend/ui-tools';
import { TypedChanges } from '@platform-frontend/util';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs';
import { ACCOUNT_UI_MATOMO_EVENT_CATEGORY_ACCOUNT } from '../account-ui-matomo-events';
import { ACCOUNT_UI_TRANSLOCO_SCOPE } from '../account-ui-transloco-scope';

interface SignInForm extends FormWithEmail {
  password: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'yat-sign-in-form',
  templateUrl: './sign-in-form.component.html',
  styleUrls: ['./sign-in-form.component.scss'],
  providers: [provideTranslocoScope(ACCOUNT_UI_TRANSLOCO_SCOPE)]
})
export class SignInFormComponent implements OnChanges, OnInit {
  constructor(private emailAuthenticationService: EmailAuthenticationService, private store: Store) {}

  @Output()
  signIn: EventEmitter<Credentials> = new EventEmitter();

  @Output()
  forgotPasswordMethod: EventEmitter<void> = new EventEmitter();

  @Input()
  spinnerActionFilter: ActionFilter = new ExclusiveActionFilter();

  @Input()
  emailControl: FormControl<string>;

  readonly emailValidators = [Validators.required, Validators.email];
  readonly passwordValidators = [Validators.required, CustomValidators.blacklisted(['open sesame'])];
  readonly MATOMO_EVENT_CATEGORY_ACCOUNT = ACCOUNT_UI_MATOMO_EVENT_CATEGORY_ACCOUNT;

  signInForm = new FormGroup<SignInForm>({
    email: new FormControl('', { nonNullable: true, validators: this.emailValidators }),
    password: new FormControl('', { nonNullable: true, validators: this.passwordValidators })
  });

  isSignInWithPassword$: Observable<boolean>;
  private emailEqualsLastCheck$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  ngOnInit(): void {
    this.isSignInWithPassword$ = this.store.select(LoadingState.isLoading).pipe(
      filter(loading => !loading),
      take(1),
      tap(() => this.compareEmailWithLastCheck()),
      switchMap(() => combineLatest([this.store.select(EmailAuthenticationState.passwordRequired), this.emailEqualsLastCheck$])),
      map(([passwordRequired, emailEqualsLastCheck]) => passwordRequired && emailEqualsLastCheck),
      distinctUntilChanged()
    );

    this.isSignInWithPassword$
      .pipe(
        tap(isSignInWithPassword => this.handleIsSignInWithPassword(isSignInWithPassword)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  compareEmailWithLastCheck(): void {
    this.emailEqualsLastCheck$.next(this.signInForm.controls.email.value === (this.store.selectSnapshot(EmailAuthenticationState.email) ?? ''));
  }

  private handleIsSignInWithPassword(isSignInWithPassword: boolean): void {
    if (isSignInWithPassword) {
      if (this.store.selectSnapshot(LinkAccountState.accountIdLinked)) {
        this.signInForm.controls.email.disable();
      }
      this.enableControl(this.signInForm.controls.password);
    } else {
      this.signInForm.controls.password.disable();
    }
  }

  private enableControl(control: FormControl): void {
    control.enable();
    control.clearValidators();
    control.updateValueAndValidity();
  }

  ngOnChanges(changes: TypedChanges<SignInFormComponent>): void {
    if (changes.emailControl) {
      // Replace the whole form here. Otherwise email changes are not recognized after the first unsuccessfull call of submitForm().
      this.signInForm = new FormGroup({
        email: this.emailControl,
        password: this.signInForm.controls.password
      });
    }
  }

  submitForm(value: Credentials): void {
    this.setValidators(this.signInForm.controls.password, this.passwordValidators);
    if (this.signInForm.controls.password.enabled && !this.signInForm.controls.password.errors)
      this.signIn.next({ ...value, email: this.signInForm.controls.email.value });
    else if (!this.signInForm.controls.password.enabled && !this.signInForm.controls.email.errors) this.checkEmail(value.email);
  }

  private setValidators(control: FormControl, validators: ValidatorFn[]): void {
    control.setValidators(validators);
    control.updateValueAndValidity();
  }

  forgotPassword(): void {
    this.forgotPasswordMethod.emit();
  }

  checkEmail(email: string): void {
    this.emailAuthenticationService
      .checkEmailAndReturnInputRequired(email)
      .pipe(
        tap(() => this.compareEmailWithLastCheck()),
        untilDestroyed(this)
      )
      .subscribe();
  }
}
